import { Stack } from "@mui/system";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined";
import RepartitionOutlinedIcon from "@mui/icons-material/RepartitionOutlined";
import {
  setOpenBulkActionModalForCampaign,
  setOpenMoveAnotherCampaignModal,
  setSelectedNegativeSearchTermForCampaign,
  setSingleRowIDPassToModalForCampaign,
} from "../../../../../../../../../../features/scopesTabSlice";
import BulkActionThird from "./BulkActionThird";
import NegativeSearchCampaignFilterItems from "./NegativeSearchCampaignFilterItems";
import MoveAnotherCampaign from "./MoveAnotherCampaign";
import { useSearchParams } from "react-router-dom";
function NegativeSearchCampaignFilterCard() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    negativesInTheCampaignData,
    selectedNegativeSearchTermForCampaign,
    openBulkActionModalForCampaign,
    selectedRowsForNegativesCampaign,
    openMoveAnotherCampaignModal,
  } = useSelector((state: any) => state.searchTermOperation);

  //close bulk action modal
  const handleClose = () => {
    dispatch(setOpenBulkActionModalForCampaign(false));
    dispatch(setSingleRowIDPassToModalForCampaign(""));
  };

  //open move another campaign modal
  const handleCloseMoveAnotherCampaign = () => {
    dispatch(setOpenMoveAnotherCampaignModal(false));
    dispatch(setSingleRowIDPassToModalForCampaign(""));
  };

  const clearSelectedSearchTermForCampaign = () => {
    dispatch(setSelectedNegativeSearchTermForCampaign(""));
  };

  const searchOptions = useMemo(() => {
    const set = new Set<string>();
    (negativesInTheCampaignData || []).forEach((r: any) => {
      if (typeof r?.search_term === "string") set.add(r.search_term.trim());
    });
    return Array.from(set); // ["shoes", "hats", ...]
  }, [negativesInTheCampaignData]);

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(
      setSelectedNegativeSearchTermForCampaign(searchParams.get("search"))
    );
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Paper sx={{ padding: "8px 16px", marginTop: "8px", boxShadow: "none" }}>
        <Stack
          flexDirection={"row"}
          justifyContent={"start"}
          gap={2}
          //sx={{ padding: "16px 16px 0px 16px" }}
        >
          <Autocomplete
            fullWidth
            disablePortal
            freeSolo
            data-tracker="136"
            sx={{ width: "390px" }}
            options={searchOptions}
            value={selectedNegativeSearchTermForCampaign || null} // string or null
            onChange={(_, newValue: string | null) => {
              dispatch(
                setSelectedNegativeSearchTermForCampaign(newValue ?? "")
              );
              setSearchParams((prevParams) => {
                const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                if (newValue) {
                  updatedParams.search = newValue; // Set new value
                  updatedParams.page = "0";
                } else {
                  delete updatedParams.search; // Remove from URL if cleared
                }
                return updatedParams;
              });
            }}
            getOptionLabel={(opt) => opt || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Term"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // Prevent default form submission
                    const inputValue = (
                      e.target as HTMLInputElement
                    ).value.trim();

                    if (inputValue) {
                      // Dispatch the search term
                      dispatch(
                        setSelectedNegativeSearchTermForCampaign(inputValue)
                      );

                      // Update URL params
                      setSearchParams((prevParams) => {
                        const updatedParams = Object.fromEntries(prevParams);
                        updatedParams.search = inputValue;
                        updatedParams.page = "0";
                        return updatedParams;
                      });

                      // Close the dropdown by blurring the input
                      (e.target as HTMLInputElement).blur();
                    }
                  }
                }}
              />
            )}
            size="small"
          />
          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
          >
            <NegativeSearchCampaignFilterItems
              clearSelectedSearchTerm={clearSelectedSearchTermForCampaign}
            />
            {/* --- Bulk Actions Header --- */}
            <Stack flexDirection="row" gap={1} justifyContent="space-between">
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body1">Bulk Actions:</Typography>
                <Tooltip title="Remove NST From campaign">
                  <IconButton
                    color={
                      selectedRowsForNegativesCampaign?.length > 0
                        ? "error"
                        : "default"
                    }
                    size="small"
                    disabled={selectedRowsForNegativesCampaign?.length === 0}
                    onClick={() => {
                      dispatch(setOpenBulkActionModalForCampaign(true)); //modal open
                      dispatch(setSingleRowIDPassToModalForCampaign("")); //single row id empty when bulk action
                    }}
                  >
                    <RemoveCircleOutlinedIcon
                      data-tracker="507"
                      fontSize="medium"
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove NST From Campaign and Add to Adgroup">
                  <IconButton
                    size="small"
                    color={
                      selectedRowsForNegativesCampaign?.length > 0
                        ? "success"
                        : "default"
                    }
                    disabled={selectedRowsForNegativesCampaign?.length === 0}
                    onClick={() => {
                      dispatch(setOpenMoveAnotherCampaignModal(true)); //modal open
                      dispatch(setSingleRowIDPassToModalForCampaign("")); //single row id empty when bulk action
                    }}
                  >
                    <RepartitionOutlinedIcon
                      data-tracker="510"
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
          {/* Bulk Action Dialog */}
          <BulkActionThird
            open={openBulkActionModalForCampaign}
            onClose={handleClose}
          />
          <MoveAnotherCampaign
            open={openMoveAnotherCampaignModal}
            onClose={handleCloseMoveAnotherCampaign}
          />
        </Stack>
      </Paper>
    </>
  );
}
export default NegativeSearchCampaignFilterCard;
